import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import PageBlock from '@sharedCR/common/PageBlock';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import { compose } from 'redux';

import useLoanId from 'hooks/useLoanId';
import usePendingChangesDependentFetch from 'hooks/usePendingChangesDependentFetch';
import { getConditionalArray } from 'utils/data';

import { selectIsMobile, selectIsTablet } from 'store/global/selectors';
import { getLoanKeyDatesAction } from 'store/loanApplications/actions';
import { selectLoanKeyDates } from 'store/loanApplications/selectors';

import { UnderwritingTestIds } from '../Underwriting.testIds';

import { keyDatesConfig, keyDatesNotFoundTableData } from './KeyDates.config';
import { COLUMN_WIDTH } from './KeyDates.constants';
import { tableStyles } from './KeyDates.styles';

const KeyDates = ({ isLoading, setIsLoading }) => {
  const dispatch = useDispatch();
  const loanId = useLoanId();

  const fetchData = useCallback(() => {
    if (!loanId) {
      return;
    }

    setIsLoading(true);

    dispatch(getLoanKeyDatesAction({ loanId }))
      .catch(Error)
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch, loanId, setIsLoading]);

  usePendingChangesDependentFetch(fetchData);

  const isMobile = useSelector(selectIsMobile);
  const isTablet = useSelector(selectIsTablet);

  const { tridApplicationDate, ecoaDate, mcrApplicationDate, loanApplicationDate } =
    useSelector(selectLoanKeyDates);

  const isNoContent =
    !isLoading && !tridApplicationDate && !ecoaDate && !mcrApplicationDate && !loanApplicationDate;
  const dataSource = [{ tridApplicationDate, ecoaDate, mcrApplicationDate, loanApplicationDate }];
  const formattedData = isNoContent ? keyDatesNotFoundTableData : dataSource;

  const visibleColumns = useMemo(() => {
    if (!isMobile && !isTablet) {
      return null;
    }

    return [
      'tridApplicationDate',
      'ecoaDate',
      ...getConditionalArray(isTablet, ['mcrApplicationDate', 'loanApplicationDate']),
    ];
  }, [isMobile, isTablet]);

  const config = useMemo(() => {
    if (!visibleColumns) {
      return keyDatesConfig;
    }

    const columnWidth = `${COLUMN_WIDTH / visibleColumns.length}%`;

    return Object.fromEntries(
      Object.entries(keyDatesConfig).map(([key, columnConfig]) => {
        const isVisible = visibleColumns.includes(key);

        if (isVisible) {
          return [key, { ...columnConfig, width: columnWidth, isHidden: false }];
        }

        return [key, { ...columnConfig, isHidden: true }];
      }),
    );
  }, [visibleColumns]);

  return (
    <PageBlock withDivider data-testid={UnderwritingTestIds.KEY_DATES_SECTION} title="Key Dates">
      <TableCDS
        config={config}
        customStyles={tableStyles}
        data={formattedData}
        isCustomDataDefinition={isNoContent}
        isLoading={isLoading}
      />
    </PageBlock>
  );
};

KeyDates.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(KeyDates);
